
import { Entry } from '@/models/generics'
import { errorToReadableString } from '@/utils/error'
import { defineComponent, PropType, ref, watch, onMounted, computed } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'
import { useStore } from 'vuex'

enum ResolverServiceName {
  Default = 'Directr',
  Directr = 'Directr',
  Sys = 'Sys'
}

export default defineComponent({
  name: 'SimpleLink',
  components: {},
  props: {
    id: {
      type: [String, Object],
      required: true
    },
    displayField: {
      type: String
    },
    pre: {
      type: String
    },
    post: {
      type: String
    },
    description: {
      type: String
    },
    service: {
      type: String as PropType<ResolverServiceName>,
      default: ResolverServiceName.Default
    },
    level: {
      type: Number
    },
    store: {
      type: Array as PropType<Array<Entry>>
    },
    to: {
      type: String
    },
    routeQuery: {
      type: Object
    },
    showFallback: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String
    },
    compact: {
      type: Boolean
    },
    medium: {
      type: Boolean
    },
    large: {
      type: Boolean
    }
  },
  emits: ['resolve', 'update:store'],
  setup (props, { emit }) {
    const error = ref('')
    const loading = ref(false)

    const $router = useRouter()
    const $store = useStore()
    const $resolver = $store.getters[`Services/${props.service}`]
    const resolved = ref<Entry>()
    const displayValue = computed(() => {
      return resolved.value && props.displayField
        ? resolved.value[props.displayField] || getDisplayValue(resolved.value || props.id)
        : props.showFallback
          ? getDisplayValue(resolved.value || props.id) : null
    })

    function getDisplayValue (item) {
      return props.displayField
        ? item[props.displayField] || !props.showFallback ? null : (
          ['ean', 'sku', 'id'].includes(props.displayField)
            ? item.id && `${item.id} (${props.displayField} not set, showing id)` || item.sku && `${item.sku} (${props.displayField} not set, showing sku)` || item.ean && `${item.ean} (${props.displayField} not set, showing ean)`
            : item.title && `${item.title} (${props.displayField} not set, showing title)` || item.name && `${item.name} (${props.displayField} not set, showing name)`
        )
        : item.title || item.name || item.id || item.sku || item.ean
    }

    function init (force = false) {
      if (!force && displayValue.value) return
      // if (resolved.value) return
      loading.value = true
      if (props.store) {
        const exists = props.store.find(p => p.sys.id === (typeof props.id === 'string' ? props.id : (props.id as Entry).sys.id))
        if (exists && (exists[props.displayField || ''] || getDisplayValue(exists))) {
          resolved.value = exists
          loading.value = false
          return
        }
      }

      $resolver.get('id', [typeof props.id === 'string' ? props.id : (props.id as Entry).sys.id], props.level ? { resolve: props.level } : undefined)
        .then(({ sys, items }) => {
          // console.log('resolved simple link', props.displayField, items)
          if (!items || !items[0]) {
            error.value = 'Entry not found'
            return
          }
          resolved.value = items[0]
          loading.value = false

          emit('update:store', [...(props.store || []), resolved.value])
          emit('resolve', resolved.value)
        }).catch(e => {
          error.value = errorToReadableString(e)
          loading.value = false
        })
    }

    onMounted(init)
    watch(() => props.id, value => {
      init(true)
    })
    // watch(() => props.store, (value, prev) => {
    //   console.log('store updated', props.displayField, !!resolved.value && !!resolved.value[props.displayField || ''])
    //   if(!!resolved.value && !!props.displayField && !!resolved.value[props.displayField || '']) {
    //     displayValue.value = resolved.value[props.displayField]}
    // })

    function pushToRoute () {
      if (!props.to) return
      void $router.push({ path: props.to, query: props.routeQuery || (props.to?.includes(String(props.id)) ? undefined : { id: typeof props.id === 'string' ? props.id : (props.id as Entry).sys.id }) })
    }

    return {
      displayValue,
      resolved,
      error,
      loading,
      pushToRoute,
      stringify: value => {
        switch (typeof value) {
          case 'number':
            return value.toString()
          case 'object':
            return Array.isArray(value) ? value.join(', ') : '[Object]'
          case 'boolean':
            return value ? 'Yes' : 'No'
          case 'string':
          default:
            return value
        }
      }
    }
  }
})
